import React, { useContext, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';
import { NotificationService } from 'ui-components/services/notification-service';

import { ModalPortal } from '../../../../components/modal-portal';
import { useEnableSelfMigration } from '../../../../feature-flags/hooks/use-enable-self-migration';
import { useIsSellerOnFeatureGatedPlanProfile } from '../../../../feature-gates/use-is-seller-on-feature-gated-plan-profile';
import { useBuildAddOnFeatures, useBuildMarketplaceAddOns } from '../../../../hooks/add-ons/use-build-addons';
import { useIsNonWalletAccessVisible, useIsLosingNonWalletAccess } from '../../../../hooks/non-wallet-access-hooks';
import { useScript } from '../../../../hooks/use-script';
import { useTrackYourCarriersEvent, PageEvents as YourCarriersPageEvents } from '../../../../hooks/use-track-your-carriers-event';
import Global from '../../../../store/global-selectors';
import { sellerSubscriptionPlanInfoUpdated } from '../../../../store/seller/seller.actions';
import { changePlan } from '../../api/subscription';
import { ManageSubscriptionDrawer } from '../components/manage-subscription-drawer';
import { SectionIds } from '../components/manage-subscription-drawer/constants';
import { filterLineItemsByFeatureUniqueness } from '../components/manage-subscription-drawer/helpers';
import { NO_BILLING_INFO, INSUFFICIENT_PLAN_RESOURCES, CARD_DECLINED, FAILED_UPDATE_PBS_BILLING_INFO } from '../constants';
import {
  failedToChangePlanNotification,
  cardDeclinedNotification,
  failedUpdatePBSBillingInfoNotification,
} from '../constants/notifications';
import { DEFAULT_SHIPMENT_LIMIT } from '../constants/shipment-limits';
import { useGetNumberOfExtraUserSeats } from '../hooks/use-get-number-of-extra-user-seats';
import { useGetRecommendedPlanIds } from '../hooks/use-get-recommended-plan-ids';
import { useGetSubscriptionCurrencyCode } from '../hooks/use-get-subscription-currency-code';
import { useCurrentSubscriptionShipmentLimit } from '../hooks/use-plan-details';
import { useShouldForceNewestPlans } from '../hooks/use-should-force-newest-plans';
import { useTrackEvent, PageEvents } from '../hooks/use-track-event';
import UserAddOnModal from '../user-add-on-modal';
import { getAddOnsToAddAndRemove, getCurrentLineItems, findPlanReference, getInitialShipmentLimitForPlan } from '../utils/plan-utilities';
import { useGetAddOnsToRemove as useGetMigrationAddOnsToRemove } from '../components/migrate-plan-modal/hooks/use-get-addons-to-remove';

import { usePaymentAndSubscriptionMegaContext } from './index';

export const ManageSubscriptionDrawerContext = React.createContext();
export const useManageSubscriptionDrawerContext = () => useContext(ManageSubscriptionDrawerContext);

const defaultExpandedSections = new Set([
  SectionIds.SubscriptionPlan,
  SectionIds.Users,
  SectionIds.AnalyticsLicenses,
  SectionIds.Carriers,
  SectionIds.Addons,
]);

// TODO: https://auctane.atlassian.net/browse/SPD-16439
// This context mounting independently from subscription manager means that
// state from a previous mount of the manager is carried over
/**
 * @param {object} props
 * @param {React.ReactNode} [props.children]
 * @param {boolean} [props.initialIsManageModalOpen]
 * @param {() => void} [props.onClose]
 * @param {() => boolean} [props.onInterceptClose] - Called before close/cancel. Return false to block the close.
 * @param {boolean} [props.shouldShowTrialAutoConvertedToFreeBanner]
 * @param {number} [props.desiredShipmentLimit]
 * @param {number} [props.desiredNumberOfAdditionalUsers]
 * @param {string} [props.selectedPlanId]
 * @param {object[]} [props.addOnsToAdd] Note: these are intended to be populated from redirect payloads
 * @param {object[]} [props.addOnsToRemove] Note: these are intended to be populated from redirect payloads
 * @param {import("@shipstation/financial-services-ui-components").StoredPaymentMethod[]} [props.newPaymentMethod]
 * @param {string} [props.upsellWorkflowGuid]
 * @returns {React.JSX.Element}
 */
export const ManageSubscriptionDrawerContextProvider = ({
  children,
  initialIsManageModalOpen: isManageModalOpen = false,
  onClose = noop,
  onInterceptClose,
  shouldShowTrialAutoConvertedToFreeBanner = false,
  upsellWorkflowGuid,
  highlightSection,
  ...props
}) => {
  const initialChangePayload = useMemo(
    () => ({
      selectedPlanId: props?.selectedPlanId,
      initialDesiredShipmentLimit: props?.desiredShipmentLimit,
      initialDesiredNumberOfAdditionalUsers: props?.desiredNumberOfAdditionalUsers,
      newPaymentMethod: props?.newPaymentMethod,
    }),
    [props?.desiredNumberOfAdditionalUsers, props?.desiredShipmentLimit, props?.newPaymentMethod, props?.selectedPlanId],
  );
  const dispatch = useDispatch();
  const {
    defaultSuggestedPlanId,
    selectedPlanId,
    setSelectedPlanId,
    byoaEnabled,
    setByoaEnabled,
    // NOTE: this variable name is misleading
    // This value is only used meaningfully by the  UserAddOnModal in the following 2 cases when a user changesPlan:
    // 1. Insufficient plan resources - we setDesiredNumberOfAdditionalUsers to carry over the value to UserAddOnModal
    // Otherwise, this variable has NOTHING to do with general adding/removing of users
    // Notably, the mega context has this value initialized as 0s
    // Splitting this out into separate state specific to this context to hopefully not regress the mega context
    // setDesiredNumberOfAdditionalUsers,
    // desiredNumberOfAdditionalUsers,
    pendingDateTimeUTC,
    pendingPlanIdInGrid,
    setIsUserAddOnModalOpen,
    setDesiredPlanCode,
    setBillingInfoErrorType,
    openHighVolumeDowngradeModal,
    desiredPlanCode,
    isUserAddOnModalOpen,
    billingInfoErrorType,
    closeUserAddonModal,
    reloadData: reloadBillingInfo,
    setPendingDateTimeUTC,
  } = usePaymentAndSubscriptionMegaContext();
  useScript('https://js.recurly.com/v4/recurly.js');

  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const { currentBillingPlan, suggestedPlan } = subscriptionData;
  const { addOns: marketplaceAddOns } = useBuildMarketplaceAddOns();
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const addOnFeaturesReferences = useBuildAddOnFeatures();

  const [expandedSections, setExpandedSections] = useState(defaultExpandedSections);

  const numberOfExtraUserSeats = useGetNumberOfExtraUserSeats();
  const [desiredNumberOfAdditionalUsers, setDesiredNumberOfAdditionalUsers] = useState(
    props?.desiredNumberOfAdditionalUsers ?? numberOfExtraUserSeats,
  );

  const currentAdditionalAnalyticsLicenses = currentBillingPlan?.additionalAnalyticsLicenses ?? 0;
  const [desiredNumberOfAdditionalAnalyticsLicenses, setDesiredNumberOfAdditionalAnalyticsLicenses] =
    useState(currentAdditionalAnalyticsLicenses);
  const isSellerOnFeatureGatedPlanProfile = useIsSellerOnFeatureGatedPlanProfile();
  const isSelfMigrationEnabled = useEnableSelfMigration();

  // Seller can migrate if self-migration is enabled and they have defaultProfilePlans (different from current profile)
  const canMigrate = isSelfMigrationEnabled && !!subscriptionData?.defaultProfilePlans;

  // Expired-trial and long-cancelled sellers are forced onto the newest lineup regardless of the self-migration flag
  const shouldForceNewestPlans = useShouldForceNewestPlans();

  const isOnAnnualBilling = subscriptionData?.currentBillingPlan?.isOnAnnualBilling ?? false;
  const [showAnnualPlans, setShowAnnualPlans] = useState(isOnAnnualBilling);

  // Default to migrating when the seller can migrate or is being force-migrated
  const [isMigratingToNewestPlans, setIsMigratingToNewestPlans] = useState(shouldForceNewestPlans || canMigrate);

  // Deduplicated union of allPlans + defaultProfilePlans for lookups that must span both sets
  const referencePlans = useMemo(() => {
    const all = subscriptionData?.allPlans ?? [];
    const defaults = subscriptionData?.defaultProfilePlans ?? [];
    const seen = new Set(all.map((p) => p.billingPlanId));
    return [...all, ...defaults.filter((p) => !seen.has(p.billingPlanId))];
  }, [subscriptionData?.allPlans, subscriptionData?.defaultProfilePlans]);

  const recommendedPlanIds = useGetRecommendedPlanIds({
    currentBillingPlanId: currentBillingPlan?.billingPlanId,
    showAnnualPlans,
    referencePlans,
  });

  const {
    addOnsToAdd: deltaAddOnCodesToAdd,
    addOnsToRemove: deltaAddOnCodesToRemove,
    selectedPlanIncludedFeatures,
  } = useMemo(
    () => getAddOnsToAddAndRemove(selectedPlanId, subscriptionData, marketplaceAddOns, planInfo),
    [selectedPlanId, subscriptionData, marketplaceAddOns, planInfo],
  );

  // Migration removes all add-ons except dropship/mesh - extract just the codes
  // Migration also doesn't add any marketplace add-ons (only ShipmentVolume, handled separately)
  const migrationAddOnsToRemove = useGetMigrationAddOnsToRemove();
  const migrationAddOnCodesToRemove = useMemo(() => migrationAddOnsToRemove.map((addOn) => addOn.code), [migrationAddOnsToRemove]);
  const emptyAddOnsToAdd = useMemo(() => [], []);

  const [addOnsToAddState, setAddOnsToAdd] = useState(props?.addOnsToAdd ?? deltaAddOnCodesToAdd);
  const [addOnsToRemoveState, setAddOnsToRemove] = useState(props?.addOnsToRemove ?? deltaAddOnCodesToRemove);

  // When migrating, use migration-specific logic; otherwise use standard logic
  const addOnsToAdd = isMigratingToNewestPlans ? emptyAddOnsToAdd : addOnsToAddState;
  const addOnsToRemove = isMigratingToNewestPlans ? migrationAddOnCodesToRemove : addOnsToRemoveState;
  const planCurrencyCode = useGetSubscriptionCurrencyCode();
  const initialPlanId = pendingPlanIdInGrid ?? defaultSuggestedPlanId;
  const selectedPlan = useMemo(() => findPlanReference(selectedPlanId, subscriptionData), [selectedPlanId, subscriptionData]);
  const initialPlan = useMemo(() => findPlanReference(initialPlanId, subscriptionData), [initialPlanId, subscriptionData]);

  const currentSubscriptionShipmentLimit = useCurrentSubscriptionShipmentLimit();

  const initialShipmentLimit = useMemo(() => {
    if (props?.desiredShipmentLimit) {
      return props.desiredShipmentLimit;
    }

    if (!initialPlanId || !initialPlan) {
      return DEFAULT_SHIPMENT_LIMIT;
    }

    return getInitialShipmentLimitForPlan(initialPlan, currentBillingPlan?.billingPlanId, currentSubscriptionShipmentLimit);
  }, [props.desiredShipmentLimit, initialPlanId, initialPlan, currentBillingPlan?.billingPlanId, currentSubscriptionShipmentLimit]);

  const [desiredShipmentLimit, setDesiredShipmentLimit] = useState(initialShipmentLimit);

  // Calculates the features & addOns to display in the manage subscription drawer
  const visibleLineItems = useMemo(() => {
    const currentPlanLineItems = getCurrentLineItems(subscriptionData, planInfo);
    const filteredCurrentPlanLineItems = currentPlanLineItems.reduce((acc, lineItem) => {
      const featureCode = lineItem.featureIds[0];
      const matchingMarketplaceAddOn = marketplaceAddOns.find((addOn) => addOn.code === featureCode);
      const lineItemIsIncludedInSelectedPlan = selectedPlanIncludedFeatures.some(
        ({ featureIds }) => featureIds[0] === lineItem.lineItemCode,
      );

      // If the line item is an alpha user, we want to display it
      if (lineItem.isAlphaUser) {
        acc.push({
          ...lineItem,
          addOnCode: undefined,
          deactivationDateTimeUtc: undefined,
        });
        return acc;
      }

      // If the line item is a trial, we want to display it
      if (lineItem.isTrial) {
        acc.push(lineItem);
        return acc;
      }

      // If the line item is included in the current plan, but not in the selected plan, we want to display it
      if (lineItem.isIncludedInPlan && !lineItemIsIncludedInSelectedPlan && matchingMarketplaceAddOn?.code) {
        acc.push({
          addOnCode: matchingMarketplaceAddOn.code,
          deactivationDateTimeUtc: lineItem.deactivationDateTimeUtc,
        });
        return acc;
      }

      // If the line item is a paid add-on, we want to display it
      if (lineItem.addOnCode) {
        acc.push(lineItem);
        return acc;
      }

      return acc;
    }, []);
    const combinedLineItems = [...filteredCurrentPlanLineItems, ...selectedPlanIncludedFeatures];

    return filterLineItemsByFeatureUniqueness(
      combinedLineItems,
      addOnFeaturesReferences,
      planCurrencyCode,
      isSellerOnFeatureGatedPlanProfile,
      isMigratingToNewestPlans,
    );
  }, [
    subscriptionData,
    planInfo,
    selectedPlanIncludedFeatures,
    addOnFeaturesReferences,
    planCurrencyCode,
    marketplaceAddOns,
    isSellerOnFeatureGatedPlanProfile,
    isMigratingToNewestPlans,
  ]);

  const trackSubscriptionSuccess = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_COMPLETE);
  const trackSubscriptionError = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_ERROR);
  const trackSubscriptionChangeCancel = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_CANCEL);
  const trackRemovedYourCarriersFeature = useTrackYourCarriersEvent(YourCarriersPageEvents.YOUR_CARRIERS_DEACTIVATE_COMPLETE);

  const shouldShowByoaToggle = useIsNonWalletAccessVisible();
  const isLosingByoaAccess = useIsLosingNonWalletAccess();

  const selectedPlanCode = selectedPlan?.code;

  const suggestedPlanCode = suggestedPlan?.planCode;

  const cleanUpStateOnClose = useCallback(() => {
    setSelectedPlanId(initialPlanId);
    setDesiredNumberOfAdditionalUsers(numberOfExtraUserSeats);
    setDesiredNumberOfAdditionalAnalyticsLicenses(currentAdditionalAnalyticsLicenses);
    setDesiredShipmentLimit(initialShipmentLimit);
    setByoaEnabled(shouldShowByoaToggle && !isLosingByoaAccess);

    const { addOnsToAdd: addOnsToAddReset, addOnsToRemove: addOnsToRemoveReset } = getAddOnsToAddAndRemove(
      initialPlanId,
      subscriptionData,
      marketplaceAddOns,
      planInfo,
    );
    setAddOnsToAdd(addOnsToAddReset);
    setAddOnsToRemove(addOnsToRemoveReset);
  }, [
    isLosingByoaAccess,
    planInfo,
    numberOfExtraUserSeats,
    setSelectedPlanId,
    subscriptionData,
    marketplaceAddOns,
    setByoaEnabled,
    shouldShowByoaToggle,
    initialPlanId,
    initialShipmentLimit,
    currentAdditionalAnalyticsLicenses,
  ]);

  const onCancelManageModal = useCallback(() => {
    reloadBillingInfo();
    cleanUpStateOnClose();
    trackSubscriptionChangeCancel({
      addOnUsers: desiredNumberOfAdditionalUsers,
      selectedPlanCode,
      suggestedPlanCode,
      upsellWorkflowGuid,
    });
    onClose();
  }, [
    desiredNumberOfAdditionalUsers,
    selectedPlanCode,
    suggestedPlanCode,
    trackSubscriptionChangeCancel,
    cleanUpStateOnClose,
    onClose,
    reloadBillingInfo,
    upsellWorkflowGuid,
  ]);

  const changePlanHandler = useCallback(
    async (payload, isRemovingByoa = false) => {
      try {
        const response = await changePlan(payload);
        const { pendingDateTimeUTC: pDate } = response?.data?.planInfo ?? {};
        setPendingDateTimeUTC(pDate);
        trackSubscriptionSuccess({
          ...payload,
          selectedPlanCode: payload.planCode,
          suggestedPlanCode,
          upsellWorkflowGuid,
        });

        if (isRemovingByoa) {
          trackRemovedYourCarriersFeature();
        }

        onClose();

        reloadBillingInfo();
        // Note: need to dispatch this action to call the seller api so that we can update Seller > SubscriptionPlanInfo
        // to get the new billingPlanId value if user changed plan to a BYOA plan
        dispatch(sellerSubscriptionPlanInfoUpdated());
      } catch (err) {
        const { response: { status, data: { failureReason = '' } = {} } = {} } = err;
        if (status === 400 && (failureReason === NO_BILLING_INFO || failureReason === CARD_DECLINED)) {
          if (failureReason === CARD_DECLINED) {
            NotificationService.showNotification(cardDeclinedNotification);
          }
        } else if (status === 400 && failureReason === INSUFFICIENT_PLAN_RESOURCES) {
          setDesiredPlanCode(payload.planCode);
          setIsUserAddOnModalOpen(true);
        } else if (status === 400 && failureReason === FAILED_UPDATE_PBS_BILLING_INFO) {
          NotificationService.showNotification(failedUpdatePBSBillingInfoNotification);
        } else {
          NotificationService.showNotification(failedToChangePlanNotification);
        }

        trackSubscriptionError({
          failureReason,
          status,
          addOnUsers: payload.addOnUsers,
          selectedPlanCode: payload.planCode,
          suggestedPlanCode,
          upsellWorkflowGuid,
        });
      }
    },
    [
      setPendingDateTimeUTC,
      trackSubscriptionSuccess,
      suggestedPlanCode,
      onClose,
      reloadBillingInfo,
      dispatch,
      trackRemovedYourCarriersFeature,
      trackSubscriptionError,
      setDesiredPlanCode,
      setIsUserAddOnModalOpen,
      upsellWorkflowGuid,
    ],
  );

  const value = useMemo(
    () => ({
      visibleLineItems,
      addOnsToAdd,
      addOnsToRemove,
      setAddOnsToAdd,
      setAddOnsToRemove,
      expandedSections,
      setExpandedSections,
      selectedPlanId,
      setSelectedPlanId,
      byoaEnabled,
      setByoaEnabled,
      setDesiredNumberOfAdditionalUsers,
      desiredNumberOfAdditionalUsers,
      desiredNumberOfAdditionalAnalyticsLicenses,
      setDesiredNumberOfAdditionalAnalyticsLicenses,
      pendingDateTimeUTC,
      pendingPlanIdInGrid,
      setIsUserAddOnModalOpen,
      setDesiredPlanCode,
      setBillingInfoErrorType,
      changePlanHandler,
      openHighVolumeDowngradeModal,
      desiredPlanCode,
      isUserAddOnModalOpen,
      billingInfoErrorType,
      reloadBillingInfo,
      desiredShipmentLimit,
      setDesiredShipmentLimit,
      currentSubscriptionShipmentLimit,
      isSellerOnFeatureGatedPlanProfile,
      selectedPlan,
      initialChangePayload,
      shouldShowTrialAutoConvertedToFreeBanner,
      showAnnualPlans,
      setShowAnnualPlans,
      isOnAnnualBilling,
      recommendedPlanIds,
      canMigrate,
      shouldForceNewestPlans,
      isMigratingToNewestPlans,
      setIsMigratingToNewestPlans,
      referencePlans,
      transitioningAddOnCodes: deltaAddOnCodesToAdd,
      highlightSection,
    }),
    [
      visibleLineItems,
      addOnsToAdd,
      addOnsToRemove,
      expandedSections,
      selectedPlanId,
      setSelectedPlanId,
      byoaEnabled,
      setByoaEnabled,
      desiredNumberOfAdditionalUsers,
      desiredNumberOfAdditionalAnalyticsLicenses,
      pendingDateTimeUTC,
      pendingPlanIdInGrid,
      setIsUserAddOnModalOpen,
      setDesiredPlanCode,
      setBillingInfoErrorType,
      changePlanHandler,
      openHighVolumeDowngradeModal,
      desiredPlanCode,
      isUserAddOnModalOpen,
      billingInfoErrorType,
      reloadBillingInfo,
      desiredShipmentLimit,
      currentSubscriptionShipmentLimit,
      isSellerOnFeatureGatedPlanProfile,
      selectedPlan,
      initialChangePayload,
      shouldShowTrialAutoConvertedToFreeBanner,
      showAnnualPlans,
      isOnAnnualBilling,
      recommendedPlanIds,
      canMigrate,
      shouldForceNewestPlans,
      isMigratingToNewestPlans,
      referencePlans,
      deltaAddOnCodesToAdd,
      highlightSection,
    ],
  );

  return (
    <ManageSubscriptionDrawerContext.Provider value={value}>
      {children}
      {isUserAddOnModalOpen && !isSellerOnFeatureGatedPlanProfile && !isMigratingToNewestPlans ? (
        <ModalPortal onClose={closeUserAddonModal}>
          <UserAddOnModal onClose={closeUserAddonModal} />
        </ModalPortal>
      ) : null}
      {isManageModalOpen ? (
        <ManageSubscriptionDrawer onCancel={onCancelManageModal} onClose={onClose} onInterceptClose={onInterceptClose} />
      ) : null}
    </ManageSubscriptionDrawerContext.Provider>
  );
};
